import type { AlertRule } from '@apm/contracts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccess } from '../auth/access';
import { useCoalesced } from '../hooks/useCoalesced';
import { useServiceFilter } from '../services/service-filter';
import { useLiveSelector } from '../store/useStream';
import { ALERT_STATE_LABEL, SIGNAL_LABEL } from '../terms';
import { formatCount, formatFull, formatRelative } from '../time';
import {
  Button,
  Cell,
  Chip,
  type Column,
  EmptyState,
  ErrorText,
  Field,
  LimitNote,
  Lines,
  Page,
  type Range,
  RangePicker,
  Row,
  rangeMs,
  rangeOf,
  Section,
  Select,
  Stat,
  Subsection,
  Table,
  Toggle,
} from '../ui';
import { ActiveAlerts } from './ActiveAlerts';
import { AlertTable } from './AlertTable';
import { SEVERITY_LABEL, SEVERITY_TONE } from './format';
import { isEnabled, isSupported, permissionNote, permissionOf, setEnabled } from './notify';
import { RuleEditor } from './RuleEditor';
import { useAlertRules, useAlerts } from './useAlerts';

/**
 * 화면 — 알림 (10회차 결정 5).
 *
 * 위는 지금 경보 중(실시간으로 즉시 갱신), 가운데는 걸려 있는 규칙, 아래는 이력이다.
 * 이력은 새 알림이 올 때마다 다시 읽는다.
 *
 * 19회차에 기간을 시각으로 직접 넣게 됐고(후보 #17), **규칙 목록·버린 규칙·발송 실패**가
 * 붙었다(콘솔 검토 C-12). 서버는 16회차부터 그것을 다 주는데 콘솔이 부르지 않아,
 * 규칙 파일에 오타가 나서 감시가 통째로 꺼져도 화면에 티가 나지 않았다.
 * "발화 중"은 소방 용어라 "경보 중"으로, "복구"는 "해제됨"으로 바꿨다(C-20).
 */

const PAGE_SIZE = 200;

export function AlertsPage() {
  const { admin } = useAccess();
  // 경보 목록과 알림 판 번호만 본다. 스냅샷이 올 때마다 이 화면을 다시 그리지 않는다 (D-C1)
  const liveAlerts = useLiveSelector((live) => live.alerts);
  // 이력 다시 읽기는 2초 단위로 몬다 (D-C6). 알림이 몰려도 서버 조회는 2초에 한 번이다
  const alertVersion = useCoalesced(
    useLiveSelector((live) => live.alertVersion),
    2_000,
  );
  const service = useServiceFilter();
  const serviceParam = service === '' ? undefined : service;
  const [range, setRange] = useState<Range>(() => rangeOf(24));
  const [severity, setSeverity] = useState('');
  const [state, setState] = useState('');
  const [desktop, setDesktop] = useState(() => isSupported() && isEnabled());
  // 권한은 토글을 누른 뒤에 바뀐다. 켜고 끌 때마다 다시 읽는다 (23-A회차, UI 피드백 19)
  const [permission, setPermission] = useState(permissionOf);
  // 지속 시간이 흐르는 것을 보이려면 1초마다 다시 그린다. 값 자체는 실시간으로 온다
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);

  const bounds = useMemo(() => rangeMs(range), [range]);
  /** 규칙을 적용하면 올린다. 이력이 같은 시점에 다시 읽는다 (24회차 리뷰 10) */
  const [rulesVersion, setRulesVersion] = useState(0);
  const bumpRules = useCallback(() => setRulesVersion((value) => value + 1), []);

  /**
   * 조회는 **한 번**이다. 서비스를 골랐으면 그 서비스 알림과 공통 알림(서비스 없음)을
   * `includeGlobal`로 함께 받아 화면에서 두 절로 나눈다. 전에는 공통 알림을 조건 없는 조회로
   * 따로 받아 걸렀는데, 최신 200건이 전부 서비스 알림이면 공통 알림이 0건으로 보였다.
   * 이제 서버가 두 종류만 세서 주므로 상한은 이 서비스 것과 공통 것에만 걸린다
   */
  const history = useAlerts({
    from: bounds.from,
    to: bounds.to,
    severity: severity === '' ? undefined : severity,
    state: state === '' ? undefined : state,
    service: serviceParam,
    includeGlobal: service !== '',
    limit: PAGE_SIZE,
    version: alertVersion + rulesVersion,
  });
  const serviceActive =
    service === '' ? liveAlerts : liveAlerts.filter((alert) => alert.service === service);
  const globalActive = liveAlerts.filter((alert) => alert.service === undefined);
  const { scoped: scopedHistory, global: globalHistory } = splitByService(history.rows, service);
  const loading = history.loading;
  const historyTotal = history.total;

  return (
    <Page>
      <Section
        boxed
        title={`지금 경보 중${
          serviceActive.length + (service === '' ? 0 : globalActive.length) > 0
            ? ` ${serviceActive.length + (service === '' ? 0 : globalActive.length)}건`
            : ''
        }`}
        note={[
          '규칙 파일(alerts.yml)의 조건에 걸린 값.',
          '조건이 풀리면 해제됨으로 바뀌고 목록에서 빠진다',
        ]}
        actions={
          <div className="flex flex-col items-end gap-1">
            {permission === 'unsupported' ? null : (
              <Toggle
                id="alerts-desktop"
                checked={desktop}
                onChange={async (next) => {
                  setDesktop(await setEnabled(next));
                  setPermission(permissionOf());
                }}
                label="브라우저 알림"
                hint="심각한 경보·인스턴스 사라짐·관제 서버 오류만 알린다. 일반 경고와 재기동은 이력에서 확인한다"
              />
            )}
            {permissionNote(permission, desktop) === '' ? null : (
              <p
                className={`max-w-xs whitespace-pre-line text-right text-xs ${
                  permission === 'denied' ? 'text-fail' : 'text-muted'
                }`}
              >
                {permissionNote(permission, desktop)}
              </p>
            )}
          </div>
        }
      >
        {service === '' ? (
          <ActiveAlerts alerts={serviceActive} now={now} />
        ) : (
          <div className="space-y-6">
            <Subsection title="이 서비스" note={service}>
              <ActiveAlerts alerts={serviceActive} now={now} />
            </Subsection>
            <Subsection title="전체 공통" note="특정 서비스에 속하지 않는 알림">
              <ActiveAlerts alerts={globalActive} now={now} />
            </Subsection>
          </div>
        )}
      </Section>

      {/* 규칙은 서비스에 묶이지 않는 자리라 관리자만 본다. 서버도 일반 계정에 403을 준다 (52회차) */}
      {admin ? <RulesSection onApplied={bumpRules} /> : null}

      <Section
        boxed
        title="알림 이력"
        note={[
          '경보와 해제를 한 줄로 합쳐 보여 준다.',
          '행의 링크로 요청 상세·CPU 기록·검색으로 간다',
        ]}
        badge={
          historyTotal !== undefined && historyTotal > 0
            ? `${formatCount(historyTotal)}건`
            : undefined
        }
        actions={
          <Button size="sm" onClick={history.reload} disabled={loading}>
            {loading ? '읽는 중…' : '새로고침'}
          </Button>
        }
      >
        <div className="mb-4 flex flex-wrap items-end gap-4">
          <RangePicker idPrefix="alerts" value={range} onChange={setRange} />
          <div className="flex flex-wrap gap-4">
            <Field label="심각도" htmlFor="alerts-severity">
              <Select
                id="alerts-severity"
                value={severity}
                onChange={setSeverity}
                options={[
                  { value: '', label: '전체' },
                  { value: 'critical', label: '심각' },
                  { value: 'warning', label: '경고' },
                ]}
              />
            </Field>
            <Field label="상태" htmlFor="alerts-state">
              <Select
                id="alerts-state"
                value={state}
                onChange={setState}
                options={[
                  { value: '', label: '전체' },
                  { value: 'firing', label: ALERT_STATE_LABEL.firing },
                  { value: 'resolved', label: ALERT_STATE_LABEL.resolved },
                  { value: 'notice', label: ALERT_STATE_LABEL.notice },
                ]}
              />
            </Field>
          </div>
        </div>

        {history.error !== undefined ? <ErrorText>{history.error}</ErrorText> : null}
        {/* 상한 안내는 조회 한 번에 한 번이다. 두 절이 한 응답을 나눈 것이라 따로 말할 것이 없다 */}
        {history.truncated ? <HistoryTruncated /> : null}
        {service === '' ? (
          <AlertHistoryRows
            rows={history.rows}
            loading={loading}
            error={history.error}
            now={now}
            showService
          />
        ) : (
          <div className="space-y-6">
            <Subsection title="이 서비스" note={service}>
              <AlertHistoryRows
                rows={scopedHistory}
                loading={loading}
                error={history.error}
                now={now}
              />
            </Subsection>
            <Subsection title="전체 공통" note="특정 서비스에 속하지 않는 알림">
              <AlertHistoryRows
                rows={globalHistory}
                loading={loading}
                error={history.error}
                now={now}
              />
            </Subsection>
          </div>
        )}
        {/* 상한은 응답 전체(두 절의 합)에 걸린다. 절마다 몇 건이 잘렸는지는 서버도 모른다 */}
        <LimitNote
          shown={history.rows.length}
          total={history.total}
          hint="기간을 좁히거나 심각도·상태를 골라 볼 것"
        />
      </Section>
    </Page>
  );
}

/**
 * 한 응답을 "이 서비스"와 "전체 공통"으로 나눈다. 서비스를 안 골랐으면 전부 이 서비스 쪽이다.
 * 서버가 `includeGlobal`로 두 종류만 주므로 여기서 다른 서비스 행을 만날 일은 없다
 */
export function splitByService<T extends { service?: string }>(
  rows: readonly T[],
  service: string,
): { scoped: T[]; global: T[] } {
  if (service === '') return { scoped: [...rows], global: [] };
  // 서버가 빈 이름을 undefined 로 정규화해 주지만, 화면도 같은 기준을 둔다
  const isGlobal = (row: T) => row.service === undefined || row.service.trim() === '';
  return {
    scoped: rows.filter((row) => row.service === service),
    global: rows.filter(isGlobal),
  };
}

function AlertHistoryRows({
  rows,
  loading,
  error,
  now,
  showService = false,
}: {
  rows: Parameters<typeof AlertTable>[0]['rows'];
  loading: boolean;
  error?: string;
  now: number;
  showService?: boolean;
}) {
  if (!loading && error === undefined && rows.length === 0) {
    return <EmptyState title="그 기간에 알림이 없다" hint="규칙 목록이 비어 있지 않은지도 볼 것" />;
  }
  return rows.length > 0 ? <AlertTable rows={rows} now={now} showService={showService} /> : null;
}

/** 날짜 폴더·파일 수 상한에 걸려 서버가 다 못 본 경우다. 200줄 상한과는 다른 것이다 */
function HistoryTruncated() {
  return (
    <p className="mb-3 text-xs text-fail">조회 상한에 걸려 일부만 보일 수 있다. 기간을 좁힐 것</p>
  );
}

const RULE_COLUMNS: readonly Column[] = [
  { label: '규칙 이름' },
  { label: '신호', width: 'w-52' },
  { label: '조건', width: 'w-40' },
  { label: '심각도', width: 'w-24' },
  { label: '채널', width: 'w-44' },
];

/**
 * 걸려 있는 규칙과 발송 상태 (콘솔 검토 C-12).
 * 24회차에 그 아래에 파일 편집기와 값 도움말이 붙었다(제안 9).
 */
function RulesSection({ onApplied }: { onApplied: () => void }) {
  const { loading, data, error, reload } = useAlertRules();
  const [editorExpanded, setEditorExpanded] = useState(false);
  // 적용하면 이 구획(규칙 표·통계)과 아래 이력을 함께 다시 읽는다 (24회차 리뷰 10)
  const applied = useCallback(() => {
    reload();
    onApplied();
  }, [reload, onApplied]);

  return (
    <Section
      boxed
      title="알림 규칙"
      note={data === undefined ? '관제 서버가 읽은 규칙 파일' : loadedNote(data)}
      badge={data ? `${data.rules.length}개` : undefined}
      actions={
        <Button size="sm" onClick={reload} disabled={loading}>
          {loading ? '읽는 중…' : '새로고침'}
        </Button>
      }
    >
      {error !== undefined ? <ErrorText>{error}</ErrorText> : null}

      {data !== undefined ? (
        <div className="space-y-4">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat
              label="알림 감시"
              value={data.stats.enabled ? '동작 중' : '꺼져 있다'}
              note={
                data.stats.evaluatedAt > 0
                  ? `마지막 판정 ${formatFull(data.stats.evaluatedAt)}`
                  : '아직 판정한 적 없음'
              }
              level={data.stats.enabled ? 'normal' : 'danger'}
              warnLabel="확인 필요"
            />
            <Stat
              label="지금 경보 중"
              value={`${data.stats.firing}건`}
              note={`보낸 경보 ${data.stats.fired}건 · 해제 ${data.stats.resolved}건`}
            />
            <Stat
              label="억제된 경보"
              value={`${data.stats.cooldownSuppressed + data.stats.rateSuppressed}건`}
              note="같은 경보가 이어질 때 한 번만 보낸다"
            />
            <Stat
              label="발송 실패"
              value={`${dispatchFailed(data)}건`}
              note={data.dispatch.channels.join(', ') || '채널 없음'}
              level={dispatchFailed(data) > 0 ? 'danger' : 'normal'}
              warnLabel="확인 필요"
            />
          </dl>

          {data.problems.length > 0 ? (
            <div className="rounded-xl border border-fail/40 bg-fail/5 px-4 py-3">
              <p className="text-sm font-medium text-fail">
                감시하지 못하는 규칙 {data.problems.length}개
              </p>
              <ul className="mt-2 space-y-1 text-sm text-fail">
                {data.problems.map((problem) => (
                  <li key={problem} className="break-words">
                    {problem}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-muted">
                <Lines
                  lines={[
                    '이 규칙들은 지금 감시하지 않는다.',
                    '규칙 파일을 고쳐 저장하면 서버가 바로 다시 읽는다.',
                    '앱이 값을 보내지 않아 건너뛴 것은 그 앱의 계측을 올려야 한다',
                  ]}
                />
              </p>
            </div>
          ) : null}

          {data.rules.length === 0 ? (
            <EmptyState
              title="알림 규칙이 없다"
              hint="규칙 파일이 비어 있거나 전부 읽지 못했다. 알림이 오지 않는 상태다"
            />
          ) : (
            <Table columns={RULE_COLUMNS} caption="알림 규칙" minWidth="min-w-[820px]">
              {data.rules.map((rule) => (
                <Row key={rule.id}>
                  <Cell className="break-words">{rule.name}</Cell>
                  <Cell tone="muted">{SIGNAL_LABEL[rule.signal] ?? rule.signal}</Cell>
                  <Cell className="tabular">
                    {conditionText(rule)}
                    {rule.enabled ? null : <span className="ml-1 text-fail">비활성</span>}
                  </Cell>
                  <Cell>
                    <Chip tone={SEVERITY_TONE[rule.severity]}>{SEVERITY_LABEL[rule.severity]}</Chip>
                  </Cell>
                  <Cell tone="muted">{rule.channels.join(', ') || '화면에만'}</Cell>
                </Row>
              ))}
            </Table>
          )}

          <RuleEditor
            signals={data.signals}
            onApplied={applied}
            expanded={editorExpanded}
            onExpandedChange={setEditorExpanded}
          />
        </div>
      ) : null}
    </Section>
  );
}

/** 규칙 파일을 언제 읽었는지. 파일 이름이 비어 있으면(아직 못 읽음) 시각만 낸다 */
function loadedNote(data: { file: string; loadedAt: number }): string {
  if (data.loadedAt <= 0) return '아직 규칙 파일을 읽지 못했다';
  const when = `${formatRelative(data.loadedAt)} 읽음`;
  return data.file === '' ? when : `${data.file} · ${when}`;
}

/**
 * 규칙 조건을 한 줄 한글로. 계약의 `AlertCondition`은 신호마다 쓰는 칸이 달라
 * 화면에서 한 번 편다.
 */
function conditionText(rule: AlertRule): string {
  const { condition } = rule;
  const parts: string[] = [];
  if (condition.gtMs !== undefined) parts.push(`${condition.gtMs}ms 초과`);
  if (condition.gtPct !== undefined) parts.push(`${condition.gtPct}% 초과`);
  if (condition.gt !== undefined) parts.push(`${condition.gt} 초과`);
  if (condition.minDurationMs !== undefined) parts.push(`${condition.minDurationMs}ms 이상 지속`);
  if (condition.minCount !== undefined) parts.push(`${condition.minCount}건 이상일 때만`);
  if (rule.for !== undefined && rule.for > 0) parts.push(`${rule.for}초 연속`);
  if (rule.window !== undefined) parts.push(`최근 ${rule.window}초`);
  return parts.length === 0 ? '-' : parts.join(' · ');
}

/**
 * 밖으로 못 나간 건수. 서버가 채널별 실패를 합쳐 준다 (24회차 리뷰 11).
 * 경보에 붙는 "억제됨"과는 다른 값이다 — 그쪽은 보내지 않기로 한 것이고 이쪽은 보내려다 실패한 것이다
 */
function dispatchFailed(data: { dispatch: { failed: number } }): number {
  return data.dispatch.failed;
}
